import { useEffect, useRef, useState } from 'react';

// How loud the room is right now, as one 0..1 number plus a per-speaker
// breakdown. Drives the meeting's ambient backdrop and the analog speaking
// glow on each tile.
//
// Read from LiveKit's own `participant.audioLevel` rather than by wiring up a
// Web Audio AnalyserNode per track. The SDK exports createAudioAnalyser for
// that, but it means an extra node (and an extra tap on the audio graph) for
// every participant, which is a lot of machinery to spend on scenery. The
// server already computes speaker levels and ships them with its periodic
// speaker updates, so this is information that has arrived anyway.
//
// The tradeoff is cadence: those updates land a couple of times a second, not
// per frame. Hence the smoothing below - raw values at that rate would make
// the backdrop strobe rather than breathe.

// Fast enough to catch each speaker update, slow enough to stay invisible on
// a profiler. Deliberately not requestAnimationFrame: there is no new
// information between updates, so a 60Hz loop would just re-read the same
// numbers.
const SAMPLE_INTERVAL_MS = 100;
// Exponential smoothing, per sample. Rising fast and falling slow is what
// makes it read as "the room is lively" instead of "someone's mic clipped" -
// an asymmetry worth more here than accuracy.
const ATTACK = 0.45;
const RELEASE = 0.12;

function smooth(previous, target) {
    const rate = target > previous ? ATTACK : RELEASE;
    return previous + (target - previous) * rate;
}

export function useRoomEnergy(room, { enabled = true } = {}) {
    // identity -> smoothed level, and one overall figure for the backdrop.
    const [levels, setLevels] = useState({});
    const [energy, setEnergy] = useState(0);
    const stateRef = useRef({ levels: {}, energy: 0 });

    useEffect(() => {
        if (!room || !enabled) {
            // Settle back to silence rather than freezing at whatever the
            // last reading was, so turning the effect off doesn't leave a
            // permanently lit backdrop behind.
            stateRef.current = { levels: {}, energy: 0 };
            setLevels({});
            setEnergy(0);
            return undefined;
        }

        const interval = setInterval(() => {
            const participants = [room.localParticipant, ...Array.from(room.remoteParticipants.values())];
            const next = {};
            let loudest = 0;

            participants.forEach((participant) => {
                if (!participant || participant.identity.startsWith('bot:')) return;
                // A muted participant can still report a stale level from
                // just before they muted; treat muted as silent so a tile
                // never glows at someone who cannot be heard.
                const raw = participant.isMicrophoneEnabled ? (participant.audioLevel || 0) : 0;
                const value = smooth(stateRef.current.levels[participant.identity] || 0, Math.min(1, raw));
                // Below this, a level is room tone rather than speech, and
                // keeping it would leave every tile faintly lit.
                next[participant.identity] = value > 0.01 ? value : 0;
                if (value > loudest) loudest = value;
            });

            // The loudest voice, not the sum: the backdrop should answer "is
            // someone talking" rather than "how many microphones are open",
            // which is what a sum would drift towards in a big room.
            const nextEnergy = smooth(stateRef.current.energy, loudest);
            stateRef.current = { levels: next, energy: nextEnergy };
            setLevels(next);
            setEnergy(nextEnergy < 0.01 ? 0 : nextEnergy);
        }, SAMPLE_INTERVAL_MS);

        return () => clearInterval(interval);
    }, [room, enabled]);

    return { energy, levels };
}
